import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Gift, Copy, Share2, Sparkles, Users, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Referral() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referral/me").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-8 text-center text-ink-500">Caricamento...</div>;

  const copy = () => {
    navigator.clipboard.writeText(data.referral_code);
    toast.success("Codice copiato!");
  };

  const share = async () => {
    const url = `${window.location.origin}/register?ref=${data.referral_code}`;
    const text = `🗺️ Unisciti a Odismap con il mio codice ${data.referral_code} e ottieni 100 punti bonus al primo percorso!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Odismap", text, url });
      } catch {}
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Invito copiato negli appunti!");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center shadow-tactile">
          <Gift className="text-ink-900" size={26}/>
        </div>
        <div>
          <h1 className="font-display font-extrabold text-3xl text-ink-900">Invita & Vinci</h1>
          <p className="text-ink-500 text-sm">Più amici porti, più tesori trovi</p>
        </div>
      </div>

      <Card className="card-treasure overflow-hidden p-0">
        <div className="p-6 bg-gradient-to-br from-gold/20 to-ocean/10 text-center">
          <div className="text-xs uppercase tracking-widest font-bold text-ink-500 mb-2">Il tuo codice invito</div>
          <div className="text-5xl font-display font-extrabold text-ink-900 tracking-widest mb-4" data-testid="my-referral-code">
            {data.referral_code}
          </div>
          <div className="flex gap-2 justify-center flex-wrap">
            <button onClick={copy} className="bg-white border border-outline text-ink-800 font-bold rounded-2xl py-3 px-5 flex items-center gap-2" data-testid="copy-referral-code">
              <Copy size={16}/> Copia
            </button>
            <button onClick={share} className="btn-tactile !py-3 !px-5 flex items-center gap-2" data-testid="share-referral">
              <Share2 size={16}/> Condividi
            </button>
          </div>
        </div>
        <div className="p-6 grid grid-cols-2 gap-3">
          <div className="p-4 rounded-2xl bg-gold-light border border-gold/30 text-center">
            <Sparkles className="mx-auto text-gold-hover mb-1" size={20}/>
            <div className="text-2xl font-display font-extrabold text-ink-900">+{data.invitee_bonus}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">pt al nuovo utente</div>
          </div>
          <div className="p-4 rounded-2xl bg-ocean-light border border-ocean/30 text-center">
            <Gift className="mx-auto text-ocean mb-1" size={20}/>
            <div className="text-2xl font-display font-extrabold text-ink-900">+{data.inviter_bonus}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">pt a te</div>
          </div>
        </div>
        <div className="px-6 pb-6 text-xs text-ink-700">
          I bonus vengono assegnati automaticamente quando il tuo invitato completa il <strong>primo percorso</strong>.
        </div>
      </Card>

      <Card className="card-treasure p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-2xl text-ink-900">I tuoi inviti</h2>
          <div className="flex items-center gap-2 text-sm font-bold text-ink-700">
            <Users size={16}/> {data.invitees.length}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded-2xl bg-parchment-dark text-center">
            <div className="text-2xl font-display font-extrabold text-ink-900">{data.invitees.length}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">Totali</div>
          </div>
          <div className="p-3 rounded-2xl bg-forest/10 text-center">
            <div className="text-2xl font-display font-extrabold text-forest">{data.rewarded_count}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">Premiati</div>
          </div>
          <div className="p-3 rounded-2xl bg-gold-light text-center">
            <div className="text-2xl font-display font-extrabold text-gold-hover">+{data.total_earned}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">Punti</div>
          </div>
        </div>
        {data.invitees.length === 0 ? (
          <div className="text-center py-6 px-4 bg-parchment-dark rounded-2xl border-2 border-dashed border-outline">
            <Users className="mx-auto text-ink-500 mb-2" size={32}/>
            <p className="text-ink-700 font-bold">Nessun invito ancora</p>
            <p className="text-sm text-ink-500 mt-1">Condividi il tuo codice con amici e famiglia!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.invitees.map((i) => (
              <div key={i.user_id} className="flex items-center gap-3 p-3 rounded-2xl bg-parchment-dark" data-testid={`invitee-${i.user_id}`}>
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                  {i.referral_rewarded ? <CheckCircle2 className="text-forest" size={20}/> : <Clock className="text-ink-500" size={20}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-ink-800 truncate">{i.name}</div>
                  <div className="text-xs text-ink-500 truncate">{i.email}</div>
                </div>
                {i.referral_rewarded ? (
                  <div className="font-display font-extrabold text-forest text-sm">+{data.inviter_bonus} pt</div>
                ) : (
                  <div className="text-xs text-ink-500 font-bold">In attesa</div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
